import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Alert,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Box,
    Typography,
    Switch,
    FormControlLabel,
    Divider,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import {
    deliveryAreaService,
    DeliveryArea,
    DeliveryAreaCreate,
    DeliveryAreaUpdate,
} from "../../services/deliveryAreaService";

interface DeliveryAreaManagerProps {
    open: boolean;
    onClose: () => void;
}

const emptyFormData = {
    name: "",
    delivery_charge: "",
    free_above_amount: "",
    note: "",
    is_active: true,
    display_order: "0",
};

export const DeliveryAreaManager: React.FC<DeliveryAreaManagerProps> = ({
    open,
    onClose,
}) => {
    const [areas, setAreas] = useState<DeliveryArea[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const [editingArea, setEditingArea] = useState<DeliveryArea | null>(null);
    const [formData, setFormData] = useState(emptyFormData);

    useEffect(() => {
        if (open) {
            fetchAreas();
        }
    }, [open]);

    const fetchAreas = async () => {
        try {
            setLoading(true);
            const response = await deliveryAreaService.getDeliveryAreas(0, 500, false);
            setAreas(response.items);
        } catch (err: any) {
            console.error("Failed to fetch delivery areas:", err);
            setError("Failed to load delivery areas");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");

        if (!formData.name.trim()) {
            setError("Area name is required");
            return;
        }
        const charge = Number(formData.delivery_charge);
        if (!formData.delivery_charge || Number.isNaN(charge) || charge < 0) {
            setError("Delivery charge must be a valid, non-negative amount");
            return;
        }
        let freeAboveAmount: number | null = null;
        if (formData.free_above_amount.trim()) {
            freeAboveAmount = Number(formData.free_above_amount);
            if (Number.isNaN(freeAboveAmount) || freeAboveAmount < 0) {
                setError("Free-above amount must be a valid, non-negative amount");
                return;
            }
        }

        const payload: DeliveryAreaCreate | DeliveryAreaUpdate = {
            name: formData.name.trim(),
            delivery_charge: charge,
            free_above_amount: freeAboveAmount,
            note: formData.note.trim() || null,
            is_active: formData.is_active,
            display_order: Number(formData.display_order) || 0,
        };

        try {
            setLoading(true);

            if (editingArea) {
                await deliveryAreaService.updateDeliveryArea(
                    editingArea.delivery_area_id,
                    payload,
                );
            } else {
                await deliveryAreaService.createDeliveryArea(
                    payload as DeliveryAreaCreate,
                );
            }

            setFormData(emptyFormData);
            setEditingArea(null);
            fetchAreas();
        } catch (err: any) {
            console.error("Failed to save delivery area:", err);
            setError(
                err.response?.data?.detail ||
                    err.message ||
                    "Failed to save delivery area",
            );
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (area: DeliveryArea) => {
        setEditingArea(area);
        setFormData({
            name: area.name,
            delivery_charge: String(area.delivery_charge),
            free_above_amount:
                area.free_above_amount != null ? String(area.free_above_amount) : "",
            note: area.note || "",
            is_active: area.is_active,
            display_order: String(area.display_order),
        });
    };

    const handleDelete = async (area: DeliveryArea) => {
        if (
            !window.confirm(
                `Deactivate "${area.name}"? It will no longer appear as a checkout option, but past orders that used it are unaffected.`,
            )
        ) {
            return;
        }

        try {
            setLoading(true);
            await deliveryAreaService.deleteDeliveryArea(area.delivery_area_id);
            fetchAreas();
        } catch (err: any) {
            console.error("Failed to deactivate delivery area:", err);
            setError(
                err.response?.data?.detail ||
                    err.message ||
                    "Failed to deactivate delivery area",
            );
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setFormData(emptyFormData);
        setEditingArea(null);
        setError("");
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Manage Delivery Areas</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 2 }}
                        onClose={() => setError("")}
                    >
                        {error}
                    </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {editingArea ? "Edit Area" : "Add New Area"}
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                        }}
                    >
                        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
                            <TextField
                                label="Area Name"
                                value={formData.name}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        name: e.target.value,
                                    })
                                }
                                required
                                fullWidth
                                size="small"
                            />
                            <TextField
                                label="Delivery Charge (৳)"
                                type="number"
                                value={formData.delivery_charge}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        delivery_charge: e.target.value,
                                    })
                                }
                                required
                                size="small"
                                sx={{ minWidth: 180 }}
                                inputProps={{ min: 0, step: "0.01" }}
                            />
                            <TextField
                                label="Free Above (৳, optional)"
                                type="number"
                                value={formData.free_above_amount}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        free_above_amount: e.target.value,
                                    })
                                }
                                size="small"
                                sx={{ minWidth: 180 }}
                                inputProps={{ min: 0, step: "0.01" }}
                                helperText="Blank = never free"
                            />
                        </Box>
                        <TextField
                            label='Note (shown to the customer, e.g. "Flat rate")'
                            value={formData.note}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    note: e.target.value,
                                })
                            }
                            multiline
                            rows={2}
                            fullWidth
                            size="small"
                        />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                            <TextField
                                label="Display Order"
                                type="number"
                                value={formData.display_order}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        display_order: e.target.value,
                                    })
                                }
                                size="small"
                                sx={{ width: 140 }}
                                helperText="Lower shows first in checkout"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.is_active}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                is_active: e.target.checked,
                                            })
                                        }
                                    />
                                }
                                label="Active"
                            />
                        </Box>
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={loading}
                                startIcon={editingArea ? <Edit /> : <Add />}
                            >
                                {editingArea ? "Update" : "Add"}
                            </Button>
                            {editingArea && (
                                <Button onClick={handleCancel} disabled={loading}>
                                    Cancel
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Existing Areas
                </Typography>
                <List>
                    {areas.map((area) => (
                        <ListItem
                            key={area.delivery_area_id}
                            secondaryAction={
                                <Box>
                                    <IconButton
                                        edge="end"
                                        onClick={() => handleEdit(area)}
                                        disabled={loading}
                                    >
                                        <Edit />
                                    </IconButton>
                                    <IconButton
                                        edge="end"
                                        onClick={() => handleDelete(area)}
                                        disabled={loading}
                                        color="error"
                                    >
                                        <Delete />
                                    </IconButton>
                                </Box>
                            }
                        >
                            <ListItemText
                                primary={`${area.name} — ৳${area.delivery_charge}`}
                                secondary={
                                    <>
                                        {area.free_above_amount != null && (
                                            <span>
                                                Free above ৳{area.free_above_amount} •{" "}
                                            </span>
                                        )}
                                        {area.note && (
                                            <span>{area.note} • </span>
                                        )}
                                        <span
                                            style={{
                                                color: area.is_active
                                                    ? "green"
                                                    : "gray",
                                            }}
                                        >
                                            {area.is_active ? "Active" : "Inactive"}
                                        </span>
                                    </>
                                }
                            />
                        </ListItem>
                    ))}
                    {areas.length === 0 && !loading && (
                        <ListItem>
                            <ListItemText
                                primary="No delivery areas yet"
                                secondary="Add your first area above"
                            />
                        </ListItem>
                    )}
                </List>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};
